<template>
  <section id="servicios" class="services-section py-16">
    <v-container>
      <!-- Section Header -->
      <div class="text-center mb-12">
        <v-chip
          color="primary"
          variant="flat"
          size="small"
          class="font-weight-bold mb-3 px-4"
        >
          PORTAFOLIO INMOBILIARIO CONSOLIDADO
        </v-chip>

        <h2 class="section-title mb-4">
          Nuestros Servicios en <span class="text-primary">Venta</span> & 
          <span class="text-primary">Arriendo</span>
        </h2>

        <p class="section-subtitle mx-auto text-grey-darken-1">
          Especialización absoluta en el mercado de finca raíz en el norte de Cundinamarca. Conectamos propietarios, compradores y arrendatarios bajo un modelo que prioriza la rentabilidad y la seguridad predial.
        </p>

        <!-- Category selector -->
        <div class="d-flex justify-center flex-wrap gap-3 mt-8">
          <v-btn
            v-for="cat in categories"
            :key="cat.id"
            :color="selectedCategory === cat.id ? 'primary' : ''"
            :variant="selectedCategory === cat.id ? 'flat' : 'outlined'"
            class="filter-btn font-weight-bold px-6 py-2"
            rounded="xl"
            @click="selectedCategory = cat.id"
          >
            <v-icon left size="18" class="mr-2">{{ cat.icon }}</v-icon>
            {{ cat.label }}
          </v-btn>
        </div>
      </div>

      <!-- Services Grid -->
      <v-row class="mt-4">
        <v-col
          v-for="(service, index) in filteredServices"
          :key="index"
          cols="12"
          md="6"
          lg="4"
          class="d-flex"
        >
          <div class="service-card d-flex flex-column w-100 pa-6 pa-md-7">
            <!-- Top badge and Icon -->
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="service-icon-box">
                <v-icon color="primary" size="26">{{ service.icon }}</v-icon>
              </div>
              <v-chip
                color="primary"
                variant="tonal"
                size="small"
                class="font-weight-bold"
              >
                {{ service.badgeText }}
              </v-chip>
            </div>

            <!-- Title & Description -->
            <h3 class="text-h5 font-weight-bold mb-3 text-grey-darken-4">
              {{ service.title }}
            </h3>

            <p class="text-body-2 text-grey-darken-2 mb-6 flex-grow-1">
              {{ service.description }}
            </p>

            <!-- Features bullets -->
            <div class="service-bullets mb-6">
              <div
                v-for="(item, i) in service.features"
                :key="i"
                class="d-flex align-center mb-2"
              >
                <v-icon color="primary" size="18" class="mr-2">mdi-check-circle-outline</v-icon>
                <span class="text-caption font-weight-medium text-grey-darken-3">{{ item }}</span>
              </div>
            </div>

            <v-divider class="mb-4"></v-divider>

            <!-- Action footer -->
            <a
              :href="getWhatsappUrl(service.title)"
              target="_blank"
              class="service-cta d-flex align-center justify-space-between text-decoration-none"
            >
              <span class="text-button font-weight-bold text-primary">Solicitar Asesoría</span>
              <div class="arrow-circle">
                <v-icon color="primary" size="18">mdi-arrow-right</v-icon>
              </div>
            </a>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
export default {
  name: 'ServicesSection',
  data() {
    return {
      selectedCategory: 'all',
      categories: [
        { id: 'all', label: 'Todos los Servicios', icon: 'mdi-view-grid-outline' },
        { id: 'venta', label: 'Venta de Inmuebles', icon: 'mdi-home-analytics' },
        { id: 'arriendo', label: 'Arriendo & Administración', icon: 'mdi-key-outline' },
        { id: 'respaldo', label: 'Seguridad & Estudio de Títulos', icon: 'mdi-shield-check-outline' },
      ],
      services: [
        {
          type: 'venta',
          badgeText: 'VENTA PREDIAL',
          icon: 'mdi-home-group',
          title: 'Venta de Inmuebles Residenciales & Campestres',
          description:
            'Comercialización estratégica de casas, apartamentos, fincas y lotes residenciales en el norte de Cundinamarca con máxima visibilidad en el mercado.',
          features: [
            'Valoración del precio de mercado y análisis de zona',
            'Estrategia de promoción publicitaria y marketing predial',
            'Filtro riguroso de compradores y visitas seguras',
            'Acompañamiento integral hasta la entrega y registro',
          ],
        },
        {
          type: 'arriendo',
          badgeText: 'ARRIENDOS',
          icon: 'mdi-key-variant',
          title: 'Arriendo & Administración de Inmuebles',
          description:
            'Gestión integral de alquileres con selección estricta de inquilinos y pólizas de garantía para garantizar su tranquilidad.',
          features: [
            'Estudio de solvencia crediticia y moral del arrendatario',
            'Elaboración e inventario detallado del inmueble',
            'Recaudo del canon y seguimiento de mantenimiento',
          ],
        },
        {
          type: 'venta',
          badgeText: 'VENTA & ARRIENDO',
          icon: 'mdi-domain',
          title: 'Inmuebles Comerciales e Industriales',
          description:
            'Promoción y comercialización de bodegas, locales, lotes industriales y oficinas en ejes de alta valorización.',
          features: [
            'Gestión enfocada a inversionistas y empresas de la zona',
            'Análisis de uso de suelo y licencias urbanísticas',
            'Negociación estructurada de contratos comerciales',
          ],
        },
        {
          type: 'respaldo',
          badgeText: 'RESPALDO PREDIO',
          icon: 'mdi-shield-check-outline',
          title: 'Estudio Técnico de Títulos & Tradición',
          description:
            'Evaluamos a profundidad el historial del inmueble antes de cualquier compraventa o arriendo para que usted invierta sin el más mínimo riesgo.',
          features: [
            'Análisis de 10 o más años de tradición catastral',
            'Verificación de gravámenes, hipotecas o limitaciones',
            'Concepto de seguridad patrimonial previo al cierre',
            'Redacción de promesas de compraventa blindadas',
          ],
        },
        {
          type: 'respaldo',
          badgeText: 'SANEAMIENTO',
          icon: 'mdi-map-marker-radius',
          title: 'Saneamiento & Gestión Predial (Próximamente)',
          description:
            'Resolvemos irregularidades administrativas para que su propiedad aumente su valor comercial y esté lista para negociarse.',
          features: [
            'Desenglobes, reloteos y regularización catastral',
            'Acompañamiento en licencias de construcción y normas',
            'Asesoría para levantamiento de patrimonio o hipotecas',
            'Gestión notarial expedita y sin trámites engorrosos',
          ],
        },
        {
          type: 'venta',
          badgeText: 'CONSULTORÍA',
          icon: 'mdi-handshake',
          title: 'Asesoría en Cierre Notarial & Escrituración',
          description:
            'Acompañamiento personal de principio a fin en notarías de el norte de Cundinamarca para garantizar escrituras limpias y transferencias exitosas.',
          features: [
            'Revisión minuciosa de minutas y liquidaciones notariales',
            'Coordinación de pagos, impuestos y paz y salvos',
            'Asistencia presencial en la firma de escrituras',
            'Seguimiento en la Oficina de Registro de Instrumentos',
          ],
        },
      ],
    };
  },
  computed: {
    filteredServices() {
      if (this.selectedCategory === 'all') {
        return this.services;
      }
      return this.services.filter((s) => s.type === this.selectedCategory);
    },
  },
  methods: {
    getWhatsappUrl(serviceTitle) {
      const msg = encodeURIComponent(
        `Hola León & Casallas, estoy interesado(a) en recibir asesoría para: ${serviceTitle}`
      );
      return `https://wa.me/573112160295?text=${msg}`;
    },
  },
};
</script>

<style scoped>
.services-section {
  background-color: #fafbfc;
  position: relative;
}

.section-title {
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 900;
  line-height: 1.2;
  color: #1a1a1a;
}

.section-subtitle {
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 680px;
}

.filter-btn {
  border-color: rgba(169, 7, 7, 0.3) !important;
  transition: all 0.25s ease;
}

.filter-btn:hover {
  transform: translateY(-2px);
}

.service-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.service-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.09);
  border-color: rgba(169, 7, 7, 0.3);
}

.service-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #A90707, #d32f2f);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.service-card:hover::after {
  opacity: 1;
}

.service-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(169, 7, 7, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.service-card:hover .service-icon-box {
  background: #A90707;
  transform: scale(1.05);
}

.service-card:hover .service-icon-box .v-icon {
  color: #ffffff !important;
}

.service-cta {
  cursor: pointer;
}

.arrow-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(169, 7, 7, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.service-cta:hover .arrow-circle {
  background: #A90707;
  transform: translateX(4px);
}

.service-cta:hover .arrow-circle .v-icon {
  color: #ffffff !important;
}
</style>
